import React, { useState, useEffect } from "react";
import {
    CircularProgress,
    IconButton,
    Typography,
    Box,
    Paper,
    Table,
    Tabs,
    Tab,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Stack
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { SessionInfo } from "../../data/SessionData";
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided, DraggableStateSnapshot, DroppableStateSnapshot } from '@hello-pangea/dnd';
import { getTimeSlots, getRooms, deleteSchedule, getScheduledSessions, updateSchedule, deleteTimeSlot } from "../../services/scheduleService"
import { TimeSlot} from "../../data/timeSlots"
import { Room } from "../../data/rooms";
import { v4 as uuidv4 } from "uuid";
import "./Schedule.css"
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddTimeSlotModal from "./AddTimeSlotModal";
import EditTimeSlotModal from "./EditTimeSlotModal";
import DeleteTimeSlotModal from "./DeleteTimeSlotModal";
import AddRoomModal from "./AddRoomModal";
import EditRoomModal from "./EditRoomModal";
import DeleteRoomModal from "./DeleteRoomModal";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import grey from "@mui/material/colors/grey";
import CloseIcon from '@mui/icons-material/Close';
import {EventInfo} from "../../data/eventInfo";

interface ScheduleProps {
    sessions: SessionInfo[];
    eventId: string;
    onBackButtonClick: () => void;
    currentEvent: EventInfo;
}

interface DayGroup {
    dayNumber: number;
    label: string;
    date: Date;
    timeSlots: TimeSlot[];
}

export default function Schedule({sessions, eventId, onBackButtonClick, currentEvent}: ScheduleProps) {
    const [dragSessions, setDragSessions] = useState<SessionInfo[]>(sessions || []);
    const [scheduledSessions, setScheduledSessions] = useState<
        {
            ScheduleId: string;
            EventId: string;
            RoomId: string;
            TimeSlotId: string;
            SessionId: string;
        }[]
    >([]);
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dayGroups, setDayGroups] = useState<DayGroup[]>([]);
    const [activeTab, setActiveTab] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [addSlotDate, setAddSlotDate] = useState<Date>(new Date());
    const [isAddTimeSlotModalOpen, setAddTimeSlotModalOpen] = useState(false);
    const [isEditTimeSlotModalOpen, setEditTimeSlotModalOpen] = useState(false);
    const [isDeleteTimeSlotModalOpen, setDeleteTimeSlotModalOpen] = useState(false);
    const [slotToDelete, setSlotToDelete] = useState<TimeSlot | null>(null);
    const [slotToEdit, setSlotToEdit] = useState<TimeSlot | null>(null);
    // Time Slot Options menu state
    const [tsAnchorEl, setTsAnchorEl] = useState<null | HTMLElement>(null);
    const isTsMenuOpen = Boolean(tsAnchorEl);

    const [rAnchorEl, setRAnchorEl] = useState<null | HTMLElement>(null);
    const isRMenuOpen = Boolean(rAnchorEl);

    const handleTsButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        setTsAnchorEl(e.currentTarget);
    };
    const handleTsMenuClose = () => {
        setTsAnchorEl(null);
    };

    const handleRButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        setRAnchorEl(e.currentTarget);
    };

    const handleRMenuClose = () => {
        setRAnchorEl(null);
    };


    useEffect(() => {
        const fetchRoomsAndTimes = async () => {
            setIsLoading(true);
            try {
                const slots = (await getTimeSlots(eventId)) || [];
                setDayGroups(groupTimeSlotsByDay(slots, currentEvent.StartDate, currentEvent.EndDate));

                const fetchedRooms = (await getRooms(eventId)) || [];
                setRooms(fetchedRooms);
            } catch (err) {
                console.error("Error fetching rooms and time slots:", err);
            } finally {
                setIsLoading(false);
            }
        };
        const fetchScheduledSessions = async () => {
            try {
                const data = await getScheduledSessions(eventId);
                setScheduledSessions(data || []);
                const scheduledIds = new Set(
                    (data || []).map((s: { SessionId: string }) => s.SessionId)
                );
                const remainingDragSessions = sessions.filter(s => !scheduledIds.has(s.Id));
                setDragSessions(remainingDragSessions);
            } catch (error) {
                console.error("Error fetching scheduled sessions:", error);
            }
        };
        fetchRoomsAndTimes();
        fetchScheduledSessions();
        setIsLoading(false);
    }, [eventId, currentEvent.StartDate, currentEvent.EndDate]);

    const refreshRooms = async () => {
        try {
            const rooms = await getRooms(eventId);
            setRooms(rooms || []);
        } catch (error: any) {
            console.error("Error refreshing rooms: ", error);
        }
    };

    const refreshTimeSlots = async () => {
        try {
            const slots = (await getTimeSlots(eventId)) || [];
            setTimeSlots(slots);
            setDayGroups(
                groupTimeSlotsByDay(
                    slots,
                    currentEvent.StartDate,
                    currentEvent.EndDate
                )
            );
        } catch (error: any) {
            console.error("Error refreshing time slots: ", error);
        }
    };

    interface DayGroup {
        dayNumber: number;
        label: string;
        date: Date;
        timeSlots: TimeSlot[];
    }

    function groupTimeSlotsByDay(
        slots: TimeSlot[],
        eventStart: string | Date,
        eventEnd: string | Date
    ): DayGroup[] {
        const start = new Date(eventStart);
        const end   = new Date(eventEnd);
        const msPerDay = 1000 * 60 * 60 * 24;
        const dayCount = Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;

        return Array.from({ length: dayCount }, (_, i) => {
            const date = new Date(start.getTime() + i * msPerDay);
            const label = `Day ${i + 1} (${date.toLocaleDateString(undefined, {
                month: 'short',
                day:   'numeric',
            })})`;

            const timeSlots = slots
                .filter(ts => {
                    const d = new Date(ts.StartsAt);
                    return (
                        d.getFullYear() === date.getFullYear() &&
                        d.getMonth()    === date.getMonth() &&
                        d.getDate()     === date.getDate()
                    );
                })
                .sort((a, b) =>
                    new Date(a.StartsAt).getTime() - new Date(b.StartsAt).getTime()
                );

            return {
                dayNumber: i + 1,
                label,
                date,
                timeSlots,
            };
        });
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleReorderSessions = (newOrder : any) => {
        setDragSessions(newOrder);
    };

    const formatTimeSlot = (timeSlot: TimeSlot) => {
        const startTime = new Date(timeSlot.StartsAt);
        const endTime = new Date(timeSlot.EndsAt);

        return `${startTime.toLocaleTimeString([],
            {hour: '2-digit', minute:'2-digit'})} - 
            ${endTime.toLocaleTimeString([],
            {hour: '2-digit', minute:'2-digit'})}`;
    }

    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;

        // Only handle calendar drops
        if (!destination.droppableId.startsWith("cell-")) return;

        const [_, timeSlotId, roomId] = destination.droppableId.split("--");

        const draggedSession = dragSessions.find(s => s.Id === draggableId);
        if (!draggedSession) {
            console.warn("Drag attempted from a non-sidebar session, but calendar sessions are not draggable.");
            return;
        }

        const alreadyScheduled = scheduledSessions.find(
            s => s.RoomId === roomId && s.TimeSlotId === timeSlotId
        );

        if (alreadyScheduled) return; // prevent multiple sessions in same cell

        const newScheduledSession = {
            ScheduleId: uuidv4(),
            EventId: eventId,
            RoomId: roomId,
            TimeSlotId: timeSlotId,
            SessionId: draggedSession.Id
        };

        setScheduledSessions(prev => {
            const withoutThisCell = prev.filter(
                s => !(s.RoomId === roomId && s.TimeSlotId === timeSlotId)
            );
            return [...withoutThisCell, newScheduledSession];
        });

        if (source.droppableId === "droppable") {
            setDragSessions(prev => prev.filter(s => s.Id !== draggedSession.Id));
        }
    };

    const handleSaveSchedule = async () => {
        try {
            await updateSchedule(scheduledSessions);
            setDialogOpen(false);
        } catch (error) {
            console.error("Failed to save schedule:", error);
            alert("Error saving schedule. Check the console for details.");
        }
    };

    const handleDeleteSchedule = async () => {
        try {
            await deleteSchedule(eventId);

            setScheduledSessions([]); // Clear UI

            const scheduledIds = new Set(scheduledSessions.map(s => s.SessionId));
            const restoredSessions = sessions.filter(s => scheduledIds.has(s.Id));
            setDragSessions(prev => {
                const alreadyInDrag = new Set(prev.map(s => s.Id));
                return [...prev, ...restoredSessions.filter(s => !alreadyInDrag.has(s.Id))];
            });

            setDeleteDialogOpen(false);
        } catch (error) {
            console.error("Failed to delete schedule:", error);
            alert("Error deleting schedule. Check the console for details.");
        }
    };

    const handleRemoveScheduledSession = (sessionId: string, roomId: string, timeSlotId: string) => {
        setScheduledSessions(prev =>
            prev.filter(s =>
                !(s.SessionId === sessionId && s.RoomId === roomId && s.TimeSlotId === timeSlotId)
            )
        );

        const removedSession = sessions.find(s => s.Id === sessionId);
        if (removedSession && !dragSessions.find(s => s.Id === sessionId)) {
            setDragSessions(prev => [...prev, removedSession]);
        }
    };

    const handleCopyScheduledSession = (sessionId: string) => {
        const sessionToCopy = sessions.find(s => s.Id === sessionId);
        if (sessionToCopy) {
            setDragSessions(prev => {
                // Avoid duplicating the session multiple times in the draggable list
                if (!prev.find(s => s.Id === sessionId)) {
                    return [...prev, sessionToCopy];
                }
                return prev;
            });
        }
    };
    const handleDeleteClick = (timeSlot: TimeSlot) => {
        setSlotToDelete(timeSlot);
        setDeleteTimeSlotModalOpen(true);
    };

    const renderSession = (session : SessionInfo) => (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold">
                {session.Title}
            </Typography>
            <Box className="speakers-container">
                {session.SessionSpeakers.map((speaker, index) => (
                    <Box key={index} className="speakers-container">
                        {speaker.PictureLink && (
                            <img
                                src={speaker.PictureLink}
                                alt={`${speaker.FirstName} ${speaker.LastName}`}
                                className="speaker-img"
                            />
                        )}
                        <Typography variant="body2">
                            {speaker.FirstName} {speaker.LastName}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    if (isLoading) {
        return (
            <Box
                className="loading"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 1,
                }}
            >
                <CircularProgress color="secondary" />
                <Typography>Loading…</Typography>
            </Box>
        );
    }

    const currentDaySlots = dayGroups[activeTab]?.timeSlots || [];
    const currentGroup = dayGroups[activeTab];
    const roomsForDay = currentGroup
        ? rooms.filter(r =>
        {
            const roomKey  = r.Date.slice(0, 10);
            const groupKey = currentGroup.date.toISOString().slice(0, 10);
            return roomKey === groupKey;
        })
        : [];

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Box>
                    {/* Header */}
                    <Box className="page-header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={onBackButtonClick} aria-label="back" className="back-button">
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="h4" className="header-title" sx={{ whiteSpace: 'nowrap' }}>
                                <span className="event-part">{currentEvent.Name}</span>
                                <span className="separator-part"> | </span>
                                <span className="schedule-part">SCHEDULE</span>
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                            {/*room spinner*/}
                            <>
                                <Button
                                    variant="text"
                                    onClick={handleRButtonClick}
                                    sx={{
                                        color: grey[800],
                                        textDecoration: "underline",
                                    }}
                                >
                                    Room Options
                                </Button>
                                <Menu
                                    anchorEl={rAnchorEl}
                                    open={isRMenuOpen}
                                    onClose={handleRMenuClose}
                                >
                                    <MenuItem onClick={() => { setAddModalOpen(true); handleRMenuClose(); }}>
                                        Add Room
                                    </MenuItem>
                                    <MenuItem onClick={() => {                // ← clear any pre-selected slot
                                        setEditModalOpen(true);
                                        handleRMenuClose(); }}>
                                        Edit Room
                                    </MenuItem>
                                    <MenuItem onClick={() => { setDeleteModalOpen(true); handleRMenuClose(); }}>
                                        Delete Room
                                    </MenuItem>
                                </Menu>
                            </>
                            {/*time slot spinner*/}
                            <>
                                <Button
                                    variant="text"
                                    onClick={handleTsButtonClick}
                                    sx={{
                                        color: grey[800],
                                        textDecoration: "underline",
                                    }}
                                >
                                    Time Slot Options
                                </Button>
                                <Menu
                                    anchorEl={tsAnchorEl}
                                    open={isTsMenuOpen}
                                    onClose={handleTsMenuClose}
                                >
                                    <MenuItem onClick={() => { setAddSlotDate(dayGroups[activeTab].date); setAddTimeSlotModalOpen(true);  handleTsMenuClose(); }}>
                                        Add Time Slot
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                        setSlotToEdit(null);                // ← clear any pre-selected slot
                                        setEditTimeSlotModalOpen(true);
                                        handleTsMenuClose(); }}>
                                        Edit Time Slot
                                    </MenuItem>
                                    <MenuItem onClick={() => { setSlotToDelete(null); setDeleteTimeSlotModalOpen(true); handleTsMenuClose(); }}>
                                        Delete Time Slot
                                    </MenuItem>
                                </Menu>
                            </>
                        </Stack>
                    </Box>

                    {/* --- Side-by-side layout --- */}
                    <Box className="main-container">
                        {/* Left: Session Sidebar */}
                        <Paper className="paper">
                            <Typography variant="h6" className="paper-title">APPROVED SESSIONS</Typography>
                            <Typography variant="body2" className="paper-subheading">Click and drag to schedule.</Typography>
                            <Droppable droppableId="droppable">
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`droppable-container ${snapshot.isDraggingOver ? 'droppable-container--dragging-over' : ''}`}
                                    >
                                        {dragSessions.length === 0 ? (
                                            <Typography textAlign="center" color="textSecondary">No sessions available</Typography>
                                        ) : (
                                            dragSessions.map((session, index) => (
                                                <Draggable key={session.Id} draggableId={session.Id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className={`draggable-item ${snapshot.isDragging ? 'draggable-item--dragging' : ''}`}
                                                            style={provided.draggableProps.style}
                                                        >
                                                            {renderSession(session)}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))
                                        )}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </Paper>

                        {/* Right: Schedule Table */}
                        <Box className="wrapper">
                            <TableContainer component={Paper} className="sub-wrapper">
                                {dayGroups.length > 0 && (
                                    <Box className="tab-container">
                                        <Tabs
                                            value={activeTab}
                                            onChange={handleTabChange}
                                            variant="fullWidth"
                                            className="custom-tabs">
                                            {dayGroups.map((group, index) => (
                                                <Tab key={index} label={group.label} />
                                            ))}
                                        </Tabs>
                                    </Box>
                                )}
                                <Table className="schedule" aria-label="schedule">
                                    <TableHead>
                                        <TableRow className="schedule-row">
                                            <TableCell className="header-cell" />
                                            {roomsForDay.map(room => (
                                                <TableCell key={room.RoomId} className="room-header-cell"
                                                           style={{ width: `${80 / roomsForDay.length}%` }}>
                                                    {room.Title}
                                                </TableCell>
                                            ))}

                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentDaySlots.map(timeSlot => (
                                            <TableRow key={timeSlot.TimeSlotId}>
                                                <TableCell className="time-slot-cell" onClick={() => { setSlotToEdit(timeSlot); setEditTimeSlotModalOpen(true); }} style={{ position: 'relative', cursor: 'pointer' }}>
                                                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteClick(timeSlot); }} style={{ position: 'absolute', top: 4, left: 4, padding: 2, color: 'rgba(0,0,0,0.6)' }}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                    <Box>
                                                        <Typography variant="button" className="time-slot-title">{timeSlot.Title}</Typography>
                                                        <Typography variant="body2" className="time-slot-time">{formatTimeSlot(timeSlot)}</Typography>
                                                    </Box>
                                                </TableCell>
                                                {roomsForDay.map(room => (
                                                    <TableCell key={`${timeSlot.TimeSlotId}-${room.RoomId}`} className="session-cell"
                                                               style={{ width: `${80 / roomsForDay.length}%` }}>
                                                        <Droppable droppableId={`cell--${timeSlot.TimeSlotId}--${room.RoomId}`}>
                                                            {(provided, snapshot) => (
                                                                <Box ref={provided.innerRef} {...provided.droppableProps} className={`cell-droppable ${snapshot.isDraggingOver ? 'cell-droppable--dragging-over' : ''}`}>
                                                                    {scheduledSessions
                                                                        .filter(s => s.RoomId === room.RoomId && s.TimeSlotId === timeSlot.TimeSlotId)
                                                                        .map((s, i) => {
                                                                            const session = sessions.find(sess => sess.Id === s.SessionId);
                                                                            return session ? (
                                                                                <Box key={session.Id} className="schedule-session">
                                                                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                                        {renderSession(session)}
                                                                                        <Box display="flex">
                                                                                            <IconButton onClick={() => handleCopyScheduledSession(session.Id)} size="small" sx={{ color: '#333', ml: 1 }}>
                                                                                                <ContentCopyIcon />
                                                                                            </IconButton>
                                                                                            <IconButton onClick={() => handleRemoveScheduledSession(session.Id, s.RoomId, s.TimeSlotId)} size="small" sx={{ color: '#333', ml: 1 }}>
                                                                                                <DeleteIcon />
                                                                                            </IconButton>
                                                                                        </Box>
                                                                                    </Box>
                                                                                </Box>
                                                                            ) : null;
                                                                        })}
                                                                    {provided.placeholder}
                                                                </Box>
                                                            )}
                                                        </Droppable>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                    </Box>

                </Box>

                {/* Action Buttons */}
                <Box textAlign="center" mt={4}>
                    <Button variant="contained" onClick={() => setDialogOpen(true)} sx={{ mr: 2 }}>Save Schedule</Button>
                    <Button variant="contained" onClick={() => setDeleteDialogOpen(true)}>Delete Schedule</Button>
                </Box>

                {/* Confirm Save Dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                    <DialogTitle>Confirm Save</DialogTitle>
                    <DialogContent>
                        <DialogContentText>Saving this will overwrite the existing schedule. Are you sure you want to continue?</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)} color="secondary">Cancel</Button>
                        <Button onClick={handleSaveSchedule} color="primary" variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Delete Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>This will permanently delete the entire schedule. Are you sure you want to proceed?</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">Cancel</Button>
                        <Button onClick={handleDeleteSchedule} color="error" variant="contained">Delete</Button>
                    </DialogActions>
                </Dialog>

                <AddRoomModal
                    isOpen={isAddModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    eventId={eventId}
                    refreshRooms={refreshRooms}/>
                <EditRoomModal
                    isOpen={isEditModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    allRooms={rooms}
                    refreshRooms={refreshRooms}
                />
                <DeleteRoomModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    allRooms={rooms}
                    refreshRooms={refreshRooms}
                />
                <AddTimeSlotModal
                    isOpen={isAddTimeSlotModalOpen}
                    initialDate={addSlotDate}
                    onClose={() => {
                        setAddTimeSlotModalOpen(false);
                        refreshTimeSlots();
                    }}
                    eventId={eventId}
                    eventStart={currentEvent.StartDate}
                    eventEnd={currentEvent.EndDate}
                />
                <EditTimeSlotModal
                    isOpen={isEditTimeSlotModalOpen}
                    onClose={() => {
                        setEditTimeSlotModalOpen(false);
                        refreshTimeSlots();
                        setSlotToEdit(null);
                    }}
                    timeSlots={timeSlots}
                    eventId={eventId}
                    eventStart={currentEvent.StartDate}
                    eventEnd={currentEvent.EndDate}
                    initialSlot={slotToEdit}
                />
                <DeleteTimeSlotModal
                    isOpen={isDeleteTimeSlotModalOpen}
                    onClose={() => {
                        setDeleteTimeSlotModalOpen(false);
                        setSlotToDelete(null);
                        refreshTimeSlots();
                    }}
                    timeSlots={timeSlots}
                    eventId={eventId}
                    initialSlot={slotToDelete}
                />
        </DragDropContext>
</LocalizationProvider>
);
}